// Color utility helpers shared across components
// normalizeHex: converts short hex (#abc), rgb()/rgba() strings to full #rrggbb when possible.
// Falls back to input when transformation not feasible (e.g., css var references).
export function normalizeHex(col: string): string {
  if (!col) return "#ffffff";
  const trimmed = col.trim();
  if (trimmed.startsWith("rgb")) {
    try {
      const nums = trimmed
        .replace(/rgba?\(/, "")
        .replace(/\)/, "")
        .split(",")
        .map((v) => parseInt(v.trim(), 10))
        .slice(0, 3);
      if (nums.length === 3 && nums.every((n) => !isNaN(n))) {
        return (
          "#" +
          nums
            .map((n) => {
              const h = n.toString(16).padStart(2, "0");
              return h;
            })
            .join("")
        );
      }
    } catch {}
    return "#ffffff"; // fallback for malformed rgb
  }
  if (trimmed.startsWith("#")) {
    if (trimmed.length === 4) {
      // #abc -> #aabbcc
      return (
        "#" +
        trimmed
          .slice(1)
          .split("")
          .map((c) => c + c)
          .join("")
          .toLowerCase()
      );
    }
    if (trimmed.length === 7) return trimmed.toLowerCase();
  }
  return trimmed; // may be CSS variable or named color
}

// Calculate relative luminance (0 = dark, 1 = light) for a hex/rgb color string.
// Falls back to 1 (light) if the color cannot be parsed.
export function luminance(color: string): number {
  const h = normalizeHex(color);
  if (!h.startsWith("#") || h.length !== 7) return 1;
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Derive a contrasting border color based on background brightness.
export function computeBorderColor(background: string): string {
  const l = luminance(background);
  if (l > 0.9) return "#d4d4d8"; // gray-300
  if (l > 0.75) return "#a1a1aa"; // gray-400
  if (l > 0.55) return "#6b7280"; // gray-500
  if (l > 0.3) return "#52525b"; // gray-600
  return "#9ca3af"; // slightly lighter outline for very dark backgrounds
}
